/**
 * Serviço para cálculo de valores DRIS (Dietary Reference Intakes)
 * Baseado em tabelas RDA (Recommended Dietary Allowance) do IOM
 */

export interface DRISValues {
  macronutrients: {
    protein: { value: number; unit: string; description: string };
    carbohydrates: { value: number; unit: string; description: string };
    fats: { value: number; unit: string; description: string };
  };
  vitamins: {
    vitaminA: { value: number; unit: string; description: string };
    vitaminC: { value: number; unit: string; description: string };
    vitaminD: { value: number; unit: string; description: string };
    vitaminE: { value: number; unit: string; description: string };
    vitaminK: { value: number; unit: string; description: string };
    vitaminB1: { value: number; unit: string; description: string };
    vitaminB2: { value: number; unit: string; description: string };
    vitaminB3: { value: number; unit: string; description: string };
    vitaminB6: { value: number; unit: string; description: string };
    vitaminB9: { value: number; unit: string; description: string };
    vitaminB12: { value: number; unit: string; description: string };
    biotin: { value: number; unit: string; description: string };
    pantothenicAcid: { value: number; unit: string; description: string };
  };
  minerals: {
    calcium: { value: number; unit: string; description: string };
    iron: { value: number; unit: string; description: string };
    magnesium: { value: number; unit: string; description: string };
    phosphorus: { value: number; unit: string; description: string };
    potassium: { value: number; unit: string; description: string };
    sodium: { value: number; unit: string; description: string };
    zinc: { value: number; unit: string; description: string };
    copper: { value: number; unit: string; description: string };
    manganese: { value: number; unit: string; description: string };
    selenium: { value: number; unit: string; description: string };
    chromium: { value: number; unit: string; description: string };
    iodine: { value: number; unit: string; description: string };
  };
}

export interface PatientDRISData {
  age: number;
  gender: 'male' | 'female';
  weight?: number; // kg (opcional, usado para alguns cálculos)
  height?: number; // cm (opcional, usado para alguns cálculos)
  pregnancyTrimester?: 1 | 2 | 3; // apenas para gestantes
}

/**
 * Calcula valores DRIS baseado nos dados do paciente
 */
export function calculateDRIS(data: PatientDRISData): DRISValues {
  const { age, gender, weight, pregnancyTrimester } = data;

  // Determinar faixa etária
  const isChild = age < 18;
  const isPregnant = pregnancyTrimester !== undefined;
  const isLactating = false; // Pode ser adicionado no futuro se necessário

  // Proteína: 0.8g/kg para adultos, mais para crianças e gestantes
  const proteinPerKg = isChild 
    ? (age < 4 ? 1.2 : age < 9 ? 1.0 : 0.95)
    : isPregnant 
    ? 1.1 
    : 0.8;
  const proteinValue = weight ? Math.round(weight * proteinPerKg) : (isChild ? (age < 4 ? 13 : age < 9 ? 19 : 34) : (gender === 'male' ? 56 : 46));

  // Carboidratos: mínimo 130g/dia para adultos, proporcional para crianças
  const carbsValue = isChild ? (age < 4 ? 130 : age < 9 ? 130 : 130) : 130;

  // Gorduras: mínimo 20-35% das calorias, assumindo ~2000 kcal = 44-78g, usar 50g como referência mínima
  const fatsValue = isChild ? (age < 4 ? 30 : age < 9 ? 35 : 50) : 50;

  return {
    macronutrients: {
      protein: {
        value: proteinValue,
        unit: 'g/dia',
        description: 'Proteína recomendada'
      },
      carbohydrates: {
        value: carbsValue,
        unit: 'g/dia',
        description: 'Carboidratos mínimos recomendados'
      },
      fats: {
        value: fatsValue,
        unit: 'g/dia',
        description: 'Gorduras mínimas recomendadas'
      }
    },
    vitamins: {
      vitaminA: getVitaminA(age, gender, isPregnant, isLactating),
      vitaminC: getVitaminC(age, gender, isPregnant, isLactating),
      vitaminD: getVitaminD(age, gender, isPregnant, isLactating),
      vitaminE: getVitaminE(age, gender, isPregnant, isLactating),
      vitaminK: getVitaminK(age, gender, isPregnant, isLactating),
      vitaminB1: getVitaminB1(age, gender, isPregnant, isLactating),
      vitaminB2: getVitaminB2(age, gender, isPregnant, isLactating),
      vitaminB3: getVitaminB3(age, gender, isPregnant, isLactating),
      vitaminB6: getVitaminB6(age, gender, isPregnant, isLactating),
      vitaminB9: getVitaminB9(age, gender, isPregnant, isLactating),
      vitaminB12: getVitaminB12(age, gender, isPregnant, isLactating),
      biotin: getBiotin(age, gender, isPregnant, isLactating),
      pantothenicAcid: getPantothenicAcid(age, gender, isPregnant, isLactating)
    },
    minerals: {
      calcium: getCalcium(age, gender, isPregnant, isLactating),
      iron: getIron(age, gender, isPregnant, isLactating),
      magnesium: getMagnesium(age, gender, isPregnant, isLactating),
      phosphorus: getPhosphorus(age, gender, isPregnant, isLactating),
      potassium: getPotassium(age, gender, isPregnant, isLactating),
      sodium: getSodium(age, gender, isPregnant, isLactating),
      zinc: getZinc(age, gender, isPregnant, isLactating),
      copper: getCopper(age, gender, isPregnant, isLactating),
      manganese: getManganese(age, gender, isPregnant, isLactating),
      selenium: getSelenium(age, gender, isPregnant, isLactating),
      chromium: getChromium(age, gender, isPregnant, isLactating),
      iodine: getIodine(age, gender, isPregnant, isLactating)
    }
  };
}

// Funções auxiliares para cada nutriente (valores em RDA)

function getVitaminA(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 770, unit: 'μg RAE/dia', description: 'Vitamina A' };
  if (isLactating) return { value: 1300, unit: 'μg RAE/dia', description: 'Vitamina A' };
  if (age < 4) return { value: 300, unit: 'μg RAE/dia', description: 'Vitamina A' };
  if (age < 9) return { value: 400, unit: 'μg RAE/dia', description: 'Vitamina A' };
  if (age < 14) return { value: 600, unit: 'μg RAE/dia', description: 'Vitamina A' };
  if (age < 19) return { value: gender === 'male' ? 900 : 700, unit: 'μg RAE/dia', description: 'Vitamina A' };
  return { value: gender === 'male' ? 900 : 700, unit: 'μg RAE/dia', description: 'Vitamina A' };
}

function getVitaminC(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 85, unit: 'mg/dia', description: 'Vitamina C' };
  if (isLactating) return { value: 120, unit: 'mg/dia', description: 'Vitamina C' };
  if (age < 4) return { value: 15, unit: 'mg/dia', description: 'Vitamina C' };
  if (age < 9) return { value: 25, unit: 'mg/dia', description: 'Vitamina C' };
  if (age < 14) return { value: 45, unit: 'mg/dia', description: 'Vitamina C' };
  if (age < 19) return { value: gender === 'male' ? 75 : 65, unit: 'mg/dia', description: 'Vitamina C' };
  return { value: gender === 'male' ? 90 : 75, unit: 'mg/dia', description: 'Vitamina C' };
}

function getVitaminD(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  // Vitamina D é igual para todas as idades e gêneros (15 μg = 600 UI)
  return { value: 15, unit: 'μg/dia (600 UI)', description: 'Vitamina D' };
}

function getVitaminE(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 15, unit: 'mg/dia', description: 'Vitamina E' };
  if (isLactating) return { value: 19, unit: 'mg/dia', description: 'Vitamina E' };
  if (age < 4) return { value: 6, unit: 'mg/dia', description: 'Vitamina E' };
  if (age < 9) return { value: 7, unit: 'mg/dia', description: 'Vitamina E' };
  if (age < 14) return { value: 11, unit: 'mg/dia', description: 'Vitamina E' };
  return { value: 15, unit: 'mg/dia', description: 'Vitamina E' };
}

function getVitaminK(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant || isLactating) return { value: 90, unit: 'μg/dia', description: 'Vitamina K' };
  if (age < 4) return { value: 30, unit: 'μg/dia', description: 'Vitamina K' };
  if (age < 9) return { value: 55, unit: 'μg/dia', description: 'Vitamina K' };
  if (age < 14) return { value: 60, unit: 'μg/dia', description: 'Vitamina K' };
  if (age < 19) return { value: 75, unit: 'μg/dia', description: 'Vitamina K' };
  return { value: gender === 'male' ? 120 : 90, unit: 'μg/dia', description: 'Vitamina K' };
}

function getVitaminB1(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 1.4, unit: 'mg/dia', description: 'Tiamina (B1)' };
  if (isLactating) return { value: 1.4, unit: 'mg/dia', description: 'Tiamina (B1)' };
  if (age < 4) return { value: 0.5, unit: 'mg/dia', description: 'Tiamina (B1)' };
  if (age < 9) return { value: 0.6, unit: 'mg/dia', description: 'Tiamina (B1)' };
  if (age < 14) return { value: 0.9, unit: 'mg/dia', description: 'Tiamina (B1)' };
  if (age < 19) return { value: gender === 'male' ? 1.2 : 1.0, unit: 'mg/dia', description: 'Tiamina (B1)' };
  return { value: gender === 'male' ? 1.2 : 1.1, unit: 'mg/dia', description: 'Tiamina (B1)' };
}

function getVitaminB2(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 1.4, unit: 'mg/dia', description: 'Riboflavina (B2)' };
  if (isLactating) return { value: 1.6, unit: 'mg/dia', description: 'Riboflavina (B2)' };
  if (age < 4) return { value: 0.5, unit: 'mg/dia', description: 'Riboflavina (B2)' };
  if (age < 9) return { value: 0.6, unit: 'mg/dia', description: 'Riboflavina (B2)' };
  if (age < 14) return { value: 0.9, unit: 'mg/dia', description: 'Riboflavina (B2)' };
  if (age < 19) return { value: gender === 'male' ? 1.3 : 1.0, unit: 'mg/dia', description: 'Riboflavina (B2)' };
  return { value: gender === 'male' ? 1.3 : 1.1, unit: 'mg/dia', description: 'Riboflavina (B2)' };
}

function getVitaminB3(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 18, unit: 'mg NE/dia', description: 'Niacina (B3)' };
  if (isLactating) return { value: 17, unit: 'mg NE/dia', description: 'Niacina (B3)' };
  if (age < 4) return { value: 6, unit: 'mg NE/dia', description: 'Niacina (B3)' };
  if (age < 9) return { value: 8, unit: 'mg NE/dia', description: 'Niacina (B3)' };
  if (age < 14) return { value: 12, unit: 'mg NE/dia', description: 'Niacina (B3)' };
  if (age < 19) return { value: gender === 'male' ? 16 : 14, unit: 'mg NE/dia', description: 'Niacina (B3)' };
  return { value: gender === 'male' ? 16 : 14, unit: 'mg NE/dia', description: 'Niacina (B3)' };
}

function getVitaminB6(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 1.9, unit: 'mg/dia', description: 'Piridoxina (B6)' };
  if (isLactating) return { value: 2.0, unit: 'mg/dia', description: 'Piridoxina (B6)' };
  if (age < 4) return { value: 0.5, unit: 'mg/dia', description: 'Piridoxina (B6)' };
  if (age < 9) return { value: 0.6, unit: 'mg/dia', description: 'Piridoxina (B6)' };
  if (age < 14) return { value: 1.0, unit: 'mg/dia', description: 'Piridoxina (B6)' };
  if (age < 19) return { value: gender === 'male' ? 1.3 : 1.2, unit: 'mg/dia', description: 'Piridoxina (B6)' };
  return { value: gender === 'male' ? 1.3 : 1.3, unit: 'mg/dia', description: 'Piridoxina (B6)' };
}

function getVitaminB9(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 600, unit: 'μg DFE/dia', description: 'Folato (B9)' };
  if (isLactating) return { value: 500, unit: 'μg DFE/dia', description: 'Folato (B9)' };
  if (age < 4) return { value: 150, unit: 'μg DFE/dia', description: 'Folato (B9)' };
  if (age < 9) return { value: 200, unit: 'μg DFE/dia', description: 'Folato (B9)' };
  if (age < 14) return { value: 300, unit: 'μg DFE/dia', description: 'Folato (B9)' };
  if (age < 19) return { value: 400, unit: 'μg DFE/dia', description: 'Folato (B9)' };
  return { value: 400, unit: 'μg DFE/dia', description: 'Folato (B9)' };
}

function getVitaminB12(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 2.6, unit: 'μg/dia', description: 'Cobalamina (B12)' };
  if (isLactating) return { value: 2.8, unit: 'μg/dia', description: 'Cobalamina (B12)' };
  if (age < 4) return { value: 0.9, unit: 'μg/dia', description: 'Cobalamina (B12)' };
  if (age < 9) return { value: 1.2, unit: 'μg/dia', description: 'Cobalamina (B12)' };
  if (age < 14) return { value: 1.8, unit: 'μg/dia', description: 'Cobalamina (B12)' };
  if (age < 19) return { value: 2.4, unit: 'μg/dia', description: 'Cobalamina (B12)' };
  return { value: 2.4, unit: 'μg/dia', description: 'Cobalamina (B12)' };
}

function getBiotin(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant || isLactating) return { value: 35, unit: 'μg/dia', description: 'Biotina' };
  if (age < 4) return { value: 8, unit: 'μg/dia', description: 'Biotina' };
  if (age < 9) return { value: 12, unit: 'μg/dia', description: 'Biotina' };
  if (age < 14) return { value: 20, unit: 'μg/dia', description: 'Biotina' };
  if (age < 19) return { value: 25, unit: 'μg/dia', description: 'Biotina' };
  return { value: 30, unit: 'μg/dia', description: 'Biotina' };
}

function getPantothenicAcid(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 6, unit: 'mg/dia', description: 'Ácido Pantotênico' };
  if (isLactating) return { value: 7, unit: 'mg/dia', description: 'Ácido Pantotênico' };
  if (age < 4) return { value: 2, unit: 'mg/dia', description: 'Ácido Pantotênico' };
  if (age < 9) return { value: 3, unit: 'mg/dia', description: 'Ácido Pantotênico' };
  if (age < 14) return { value: 4, unit: 'mg/dia', description: 'Ácido Pantotênico' };
  if (age < 19) return { value: 5, unit: 'mg/dia', description: 'Ácido Pantotênico' };
  return { value: 5, unit: 'mg/dia', description: 'Ácido Pantotênico' };
}

function getCalcium(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 1000, unit: 'mg/dia', description: 'Cálcio' };
  if (isLactating) return { value: 1000, unit: 'mg/dia', description: 'Cálcio' };
  if (age < 4) return { value: 700, unit: 'mg/dia', description: 'Cálcio' };
  if (age < 9) return { value: 1000, unit: 'mg/dia', description: 'Cálcio' };
  if (age < 14) return { value: 1300, unit: 'mg/dia', description: 'Cálcio' };
  if (age < 19) return { value: 1300, unit: 'mg/dia', description: 'Cálcio' };
  if (age >= 51 && gender === 'female') return { value: 1200, unit: 'mg/dia', description: 'Cálcio' };
  if (age >= 71) return { value: 1200, unit: 'mg/dia', description: 'Cálcio' };
  return { value: 1000, unit: 'mg/dia', description: 'Cálcio' };
}

function getIron(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 27, unit: 'mg/dia', description: 'Ferro' };
  if (isLactating) return { value: 9, unit: 'mg/dia', description: 'Ferro' };
  if (age < 4) return { value: 7, unit: 'mg/dia', description: 'Ferro' };
  if (age < 9) return { value: 10, unit: 'mg/dia', description: 'Ferro' };
  if (age < 14) return { value: 8, unit: 'mg/dia', description: 'Ferro' };
  if (age < 19) return { value: gender === 'male' ? 11 : 15, unit: 'mg/dia', description: 'Ferro' };
  if (age < 51) return { value: gender === 'male' ? 8 : 18, unit: 'mg/dia', description: 'Ferro' };
  return { value: 8, unit: 'mg/dia', description: 'Ferro' };
}

function getMagnesium(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 350, unit: 'mg/dia', description: 'Magnésio' };
  if (isLactating) return { value: 310, unit: 'mg/dia', description: 'Magnésio' };
  if (age < 4) return { value: 80, unit: 'mg/dia', description: 'Magnésio' };
  if (age < 9) return { value: 130, unit: 'mg/dia', description: 'Magnésio' };
  if (age < 14) return { value: gender === 'male' ? 240 : 240, unit: 'mg/dia', description: 'Magnésio' };
  if (age < 19) return { value: gender === 'male' ? 410 : 360, unit: 'mg/dia', description: 'Magnésio' };
  if (age < 31) return { value: gender === 'male' ? 400 : 310, unit: 'mg/dia', description: 'Magnésio' };
  if (age < 51) return { value: gender === 'male' ? 420 : 320, unit: 'mg/dia', description: 'Magnésio' };
  return { value: gender === 'male' ? 420 : 320, unit: 'mg/dia', description: 'Magnésio' };
}

function getPhosphorus(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant || isLactating) return { value: 700, unit: 'mg/dia', description: 'Fósforo' };
  if (age < 4) return { value: 460, unit: 'mg/dia', description: 'Fósforo' };
  if (age < 9) return { value: 500, unit: 'mg/dia', description: 'Fósforo' };
  if (age < 14) return { value: 1250, unit: 'mg/dia', description: 'Fósforo' };
  if (age < 19) return { value: 1250, unit: 'mg/dia', description: 'Fósforo' };
  return { value: 700, unit: 'mg/dia', description: 'Fósforo' };
}

function getPotassium(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 2900, unit: 'mg/dia', description: 'Potássio' };
  if (isLactating) return { value: 2800, unit: 'mg/dia', description: 'Potássio' };
  if (age < 4) return { value: 3000, unit: 'mg/dia', description: 'Potássio' };
  if (age < 9) return { value: 3800, unit: 'mg/dia', description: 'Potássio' };
  if (age < 14) return { value: gender === 'male' ? 4500 : 4500, unit: 'mg/dia', description: 'Potássio' };
  if (age < 19) return { value: gender === 'male' ? 4700 : 4700, unit: 'mg/dia', description: 'Potássio' };
  return { value: gender === 'male' ? 4700 : 4700, unit: 'mg/dia', description: 'Potássio' };
}

function getSodium(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  // Limite máximo recomendado (não RDA, mas AI - Adequate Intake)
  if (age < 4) return { value: 1200, unit: 'mg/dia', description: 'Sódio (limite máximo)' };
  if (age < 9) return { value: 1500, unit: 'mg/dia', description: 'Sódio (limite máximo)' };
  if (age < 14) return { value: 1800, unit: 'mg/dia', description: 'Sódio (limite máximo)' };
  if (age < 19) return { value: 2300, unit: 'mg/dia', description: 'Sódio (limite máximo)' };
  return { value: 2300, unit: 'mg/dia', description: 'Sódio (limite máximo)' };
}

function getZinc(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 11, unit: 'mg/dia', description: 'Zinco' };
  if (isLactating) return { value: 12, unit: 'mg/dia', description: 'Zinco' };
  if (age < 4) return { value: 3, unit: 'mg/dia', description: 'Zinco' };
  if (age < 9) return { value: 5, unit: 'mg/dia', description: 'Zinco' };
  if (age < 14) return { value: 8, unit: 'mg/dia', description: 'Zinco' };
  if (age < 19) return { value: gender === 'male' ? 11 : 9, unit: 'mg/dia', description: 'Zinco' };
  return { value: gender === 'male' ? 11 : 8, unit: 'mg/dia', description: 'Zinco' };
}

function getCopper(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 1.0, unit: 'mg/dia', description: 'Cobre' };
  if (isLactating) return { value: 1.3, unit: 'mg/dia', description: 'Cobre' };
  if (age < 4) return { value: 0.34, unit: 'mg/dia', description: 'Cobre' };
  if (age < 9) return { value: 0.44, unit: 'mg/dia', description: 'Cobre' };
  if (age < 14) return { value: 0.7, unit: 'mg/dia', description: 'Cobre' };
  if (age < 19) return { value: 0.89, unit: 'mg/dia', description: 'Cobre' };
  return { value: 0.9, unit: 'mg/dia', description: 'Cobre' };
}

function getManganese(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant || isLactating) return { value: 2.0, unit: 'mg/dia', description: 'Manganês' };
  if (age < 4) return { value: 1.2, unit: 'mg/dia', description: 'Manganês' };
  if (age < 9) return { value: 1.5, unit: 'mg/dia', description: 'Manganês' };
  if (age < 14) return { value: gender === 'male' ? 1.9 : 1.6, unit: 'mg/dia', description: 'Manganês' };
  if (age < 19) return { value: gender === 'male' ? 2.2 : 1.6, unit: 'mg/dia', description: 'Manganês' };
  return { value: gender === 'male' ? 2.3 : 1.8, unit: 'mg/dia', description: 'Manganês' };
}

function getSelenium(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 60, unit: 'μg/dia', description: 'Selênio' };
  if (isLactating) return { value: 70, unit: 'μg/dia', description: 'Selênio' };
  if (age < 4) return { value: 20, unit: 'μg/dia', description: 'Selênio' };
  if (age < 9) return { value: 30, unit: 'μg/dia', description: 'Selênio' };
  if (age < 14) return { value: 40, unit: 'μg/dia', description: 'Selênio' };
  if (age < 19) return { value: 55, unit: 'μg/dia', description: 'Selênio' };
  return { value: 55, unit: 'μg/dia', description: 'Selênio' };
}

function getChromium(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 30, unit: 'μg/dia', description: 'Cromo' };
  if (isLactating) return { value: 45, unit: 'μg/dia', description: 'Cromo' };
  if (age < 4) return { value: 11, unit: 'μg/dia', description: 'Cromo' };
  if (age < 9) return { value: 15, unit: 'μg/dia', description: 'Cromo' };
  if (age < 14) return { value: gender === 'male' ? 25 : 21, unit: 'μg/dia', description: 'Cromo' };
  if (age < 19) return { value: gender === 'male' ? 35 : 24, unit: 'μg/dia', description: 'Cromo' };
  if (age < 51) return { value: gender === 'male' ? 35 : 25, unit: 'μg/dia', description: 'Cromo' };
  return { value: gender === 'male' ? 30 : 20, unit: 'μg/dia', description: 'Cromo' };
}

function getIodine(age: number, gender: string, isPregnant: boolean, isLactating: boolean) {
  if (isPregnant) return { value: 220, unit: 'μg/dia', description: 'Iodo' };
  if (isLactating) return { value: 290, unit: 'μg/dia', description: 'Iodo' };
  if (age < 4) return { value: 90, unit: 'μg/dia', description: 'Iodo' };
  if (age < 9) return { value: 90, unit: 'μg/dia', description: 'Iodo' };
  if (age < 14) return { value: 120, unit: 'μg/dia', description: 'Iodo' };
  if (age < 19) return { value: 150, unit: 'μg/dia', description: 'Iodo' };
  return { value: 150, unit: 'μg/dia', description: 'Iodo' };
}






